import { Theme } from '@mui/material/styles';

/**
 * Common layout styling utilities for consistent layout patterns across the application
 * These styles extract patterns found in multiple components for consistent spacing and layout
 */

/**
 * Flexbox utility styles - commonly used flex patterns
 */
export const createFlexStyles = (theme: Theme) => ({
  // Basic flex containers
  flexCenter: {
    display: 'flex',
    alignItems: 'center'
  },
  flexCenterBetween: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  flexCenterEnd: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end'
  },
  flexColumn: {
    display: 'flex',
    flexDirection: 'column' as const
  },
  flexColumnCenter: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center'
  },
  // Flex with gaps
  flexCenterGap1: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  flexCenterGap2: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  }
});

/**
 * Column container styles - for main content layout
 */
export const createColumnStyles = (theme: Theme) => ({
  // Standard column containers (25% width flex containers)
  columnContainer: {
    flex: '1 1 20%',
    height: 'fit-content',
    // padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius
  },
  // Config/settings column with grey background
  configColumn: {
    flex: '1 1 25%',
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    height: 'fit-content'
  },
  // SME column with warning border
  smeColumn: {
    flex: '1 1 30%',
    border: `1px solid ${theme.palette.warning.main}`,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    height: 'fit-content'
  },
  // Primary column with primary border
  primaryColumn: {
    flex: '1 1 25%',
    border: `1px solid ${theme.palette.primary.main}`,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    height: 'fit-content'
  }
});

/**
 * Header and navigation styles
 */
export const createHeaderStyles = (theme: Theme) => ({
  // Page header container
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(1)
  },
  // Section header with divider
  sectionHeader: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    gap: theme.spacing(1)
  },
  // Navigation container
  sectionNavigation: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`
  }
});

/**
 * Button group and control styles
 */
export const createControlStyles = (theme: Theme) => ({
  // Button groups
  buttonGroup: {
    display: 'flex',
    gap: theme.spacing(1),
    alignSelf: 'flex-end',
    width: '100%',
    justifyContent: 'flex-end'
  },
  // Section controls with border
  sectionControls: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`
  },
  // Options group with background
  optionsGroup: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    width: '100%',
    // border: `1px solid ${theme.palette.divider}`,
    // padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    flexWrap: 'wrap' as const
  }
});

/**
 * Creates spacing utility styles for consistent margins and padding
 */
export const createSpacingStyles = (theme: Theme) => ({
  // Margin bottom utilities
  marginBottom1: {
    marginBottom: theme.spacing(1)
  },
  marginBottom2: {
    marginBottom: theme.spacing(2) 
  },
  marginBottom3: {
    marginBottom: theme.spacing(3)
  },
  marginBottom4: {
    marginBottom: theme.spacing(4)
  },
  
  // Margin bottom short utilities (for sx props)
  mb1: { mb: 1 },
  mb2: { mb: 2 },
  mb3: { mb: 3 },
  mb4: { mb: 4 },
  
  // Common spacing containers
  sectionSpacing: {
    marginBottom: theme.spacing(3)
  },
  fieldSpacing: {
    marginBottom: theme.spacing(2)
  },
  compactSpacing: {
    marginBottom: theme.spacing(1)
  },
  // Sizing utilities
  fullWidth: {
    width: '100%'
  },
  fitContent: {
    height: 'fit-content'
  },
  // Common container styles
  pageContainer: {
    padding: theme.spacing(3),
    width: '100%',
    maxWidth: '100%'
  }
});

/**
 * Creates card and paper layout utilities
 */
export const createCardStyles = (theme: Theme) => ({
  // Basic card with paper background
  basicCard: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1]
  },
  
  // Card with border instead of shadow
  borderedCard: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`
  },
  
  // Light grey background card
  lightCard: {
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`
  },
  
  // Code/info background card
  codeCard: {
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`
  },
  
  // Section container with padding
  sectionCard: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3)
  },
  
  // Filter container style
  filterCard: {
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(3),
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
  }
});

/**
 * Creates content container and state utilities
 */
export const createContentStyles = (theme: Theme) => ({
  // Center-aligned content container
  centerContent: {
    textAlign: 'center' as const,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  
  // Loading state container
  loadingState: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
    minHeight: 200
  },
  
  // Empty state container
  emptyState: {
    textAlign: 'center' as const,
    padding: theme.spacing(4),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px dashed ${theme.palette.grey[300]}`
  },
  
  // Full width content wrapper
  contentWrapper: {
    width: '100%',
    maxWidth: '98%',
    margin: '0 auto'
  },
  
  // Form content container
  formContent: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3)
  }
});

/**
 * Creates text alignment utilities
 */
export const createTextStyles = (theme: Theme) => ({
  // Text alignment
  textCenter: { textAlign: 'center' as const },
  textLeft: { textAlign: 'left' as const },
  textRight: { textAlign: 'right' as const },
  
  // Font weights with theme consistency
  fontBold: { fontWeight: 'bold' },
  fontMedium: { fontWeight: 500 },
  fontNormal: { fontWeight: 'normal' },
  
  // Common typography patterns
  sectionTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(2)
  },
  fieldLabel: {
    fontWeight: 500,
    marginBottom: theme.spacing(0.5)
  }
});

/**
 * Creates background utilities for consistent theming
 */
export const createBackgroundStyles = (theme: Theme) => ({
  // Paper backgrounds
  paperBackground: {
    backgroundColor: theme.palette.background.paper
  },
  
  // Grey backgrounds
  lightGreyBackground: {
    backgroundColor: theme.palette.grey[50]
  },
  greyBackground: {
    backgroundColor: theme.palette.grey[100]
  },
  
  // Default background
  defaultBackground: {
    backgroundColor: theme.palette.background.default
  },
  
  // Semantic backgrounds with consistent colors
  hotMediaBackground: {
    backgroundColor: theme.palette.semantic?.hotMedia || '#fff5f5'
  },
  coldMediaBackground: {
    backgroundColor: theme.palette.semantic?.coldMedia || '#f0f8ff'
  }
});

/**
 * Loading and status styles
 */
export const createStatusStyles = (theme: Theme) => ({
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3)
  },
  errorContainer: {
    textAlign: 'center' as const,
    width: '100%',
    display: 'block'
  },
  loadingText: {
    marginLeft: theme.spacing(2)
  }
}); 